import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  ArrowLeft, Upload, Move, ZoomIn, ZoomOut, Trash2, Loader2,
  Sparkles, Send, Shirt, CheckCircle2,
} from 'lucide-react';
import { brandName, brandEmail, brandPhone, brandLogo, brandWebsite } from '../lib/brand';
import { BrandLogo } from '../components/BrandLogo';

// Simple SVG t-shirt silhouette — color-fillable via CSS currentColor.
const TShirtSVG = ({ color, view = 'front' }) => (
  <svg
    viewBox="0 0 400 480"
    width="100%"
    height="100%"
    style={{ color }}
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
        <feGaussianBlur in="SourceAlpha" stdDeviation="4" />
        <feOffset dx="2" dy="4" result="offsetblur" />
        <feComponentTransfer><feFuncA type="linear" slope="0.35" /></feComponentTransfer>
        <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
      </filter>
    </defs>
    {/* Body */}
    <path
      d="M 110 90 Q 130 60 175 55 Q 200 75 225 55 Q 270 60 290 90 L 360 130 L 330 200 L 295 180 L 295 440 Q 200 460 105 440 L 105 180 L 70 200 L 40 130 Z"
      fill="currentColor"
      stroke="rgba(0,0,0,0.35)"
      strokeWidth="1.5"
      filter="url(#shadow)"
    />
    {/* Collar */}
    {view === 'front' ? (
      <path d="M 175 55 Q 200 90 225 55" fill="rgba(0,0,0,0.18)" stroke="rgba(0,0,0,0.35)" strokeWidth="1.5" />
    ) : (
      <path d="M 175 55 Q 200 70 225 55" fill="rgba(0,0,0,0.12)" stroke="rgba(0,0,0,0.35)" strokeWidth="1.5" />
    )}
    {/* Seam stitching subtle */}
    <path d="M 105 180 L 105 440" fill="none" stroke="rgba(0,0,0,0.10)" strokeWidth="1" strokeDasharray="3 4" />
    <path d="M 295 180 L 295 440" fill="none" stroke="rgba(0,0,0,0.10)" strokeWidth="1" strokeDasharray="3 4" />
  </svg>
);

const GARMENT_COLORS = [
  { name: 'Black', hex: '#0E0E0E' },
  { name: 'White', hex: '#F8F8F8' },
  { name: 'Heather Gray', hex: '#9CA3AF' },
  { name: 'Navy', hex: '#1E2A56' },
  { name: 'Royal', hex: '#1E3A8A' },
  { name: 'Red', hex: '#B91C1C' },
  { name: 'Maroon', hex: '#6B1421' },
  { name: 'Forest', hex: '#1F3B2C' },
  { name: 'Sand', hex: '#D7C9A6' },
  { name: 'Gold', hex: '#D4A017' },
];

const METHODS = [
  { id: 'screen_print', label: 'Screen Print', hint: 'Best for 24+ shirts' },
  { id: 'embroidery', label: 'Embroidery', hint: 'Premium stitched logos' },
  { id: 'dtf', label: 'DTF Print', hint: 'Photo-real, any color' },
  { id: 'dtg', label: 'DTG', hint: 'Small batches' },
  { id: 'sublimation', label: 'Sublimation', hint: 'Performance polyester' },
];

const DESIGN_AREAS = [
  { id: 'front_center', label: 'Front Center', x: 50, y: 45 },
  { id: 'left_chest', label: 'Left Chest', x: 35, y: 32 },
  { id: 'full_back', label: 'Full Back', x: 50, y: 55 },
];

const PublicDesigner = () => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  // Read pre-selected garment from ?styleID=…&color=…&imageUrl=… (routed from
  // the /catalog page). When present the canvas swaps from the generic SVG
  // t-shirt to the real product photo.
  const searchParams = new URLSearchParams(window.location.search);
  const preSelected = searchParams.get('styleID') ? {
    styleID: searchParams.get('styleID'),
    styleName: searchParams.get('styleName') || '',
    brand: searchParams.get('brand') || '',
    color: searchParams.get('color') || '',
    colorHex: searchParams.get('colorHex') || '',
    imageUrl: searchParams.get('imageUrl') || '',
    basePrice: parseFloat(searchParams.get('basePrice') || '0') || 0,
  } : null;

  const [garmentColor, setGarmentColor] = useState(preSelected?.colorHex || '#0E0E0E');
  const [view] = useState('front');
  const [logoSrc, setLogoSrc] = useState(null);
  const [logoPos, setLogoPos] = useState({ x: 50, y: 42, size: 28 }); // % units
  const [dragging, setDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const [quantity, setQuantity] = useState(48);
  const [method, setMethod] = useState('screen_print');
  const [numLocations, setNumLocations] = useState(1);
  const [colors, setColors] = useState(2);
  const [garmentPrice] = useState(7.5); // estimate baseline

  const [pricing, setPricing] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(false);

  const [contact, setContact] = useState({
    name: '', email: '', phone: '', organization: '', deadline: '', notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Fetch live estimate whenever inputs change. Fires immediately on mount
  // so the panel never shows the "Enter your quantity…" empty state on load.
  const isFirstFetch = useRef(true);
  useEffect(() => {
    const delay = isFirstFetch.current ? 0 : 250;
    isFirstFetch.current = false;
    const t = setTimeout(async () => {
      try {
        setPricingLoading(true);
        const res = await axios.post('/public/pricing/estimate', {
          quantity: Number(quantity) || 0,
          print_method: method,
          num_locations: Number(numLocations) || 1,
          colors: Number(colors) || 1,
          garment_price: garmentPrice,
        });
        setPricing(res.data);
      } catch (e) {
        // silent — keep last value
      } finally {
        setPricingLoading(false);
      }
    }, delay);
    return () => clearTimeout(t);
  }, [quantity, method, numLocations, colors, garmentPrice]);

  const onUploadLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      toast.error('Logo too large — please keep under 6MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setLogoSrc(ev.target.result);
    reader.readAsDataURL(file);
  };

  const startDrag = (e) => {
    e.preventDefault();
    const rect = canvasRef.current.getBoundingClientRect();
    const isTouch = e.touches?.length > 0;
    const cx = (isTouch ? e.touches[0].clientX : e.clientX) - rect.left;
    const cy = (isTouch ? e.touches[0].clientY : e.clientY) - rect.top;
    const px = (logoPos.x / 100) * rect.width;
    const py = (logoPos.y / 100) * rect.height;
    setDragOffset({ x: cx - px, y: cy - py });
    setDragging(true);
  };

  const onDrag = (e) => {
    if (!dragging) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const isTouch = e.touches?.length > 0;
    const cx = (isTouch ? e.touches[0].clientX : e.clientX) - rect.left;
    const cy = (isTouch ? e.touches[0].clientY : e.clientY) - rect.top;
    const nx = ((cx - dragOffset.x) / rect.width) * 100;
    const ny = ((cy - dragOffset.y) / rect.height) * 100;
    setLogoPos((p) => ({
      ...p,
      x: Math.max(5, Math.min(95, nx)),
      y: Math.max(5, Math.min(95, ny)),
    }));
  };

  const endDrag = () => setDragging(false);

  // Snap-to-placement helper
  const snapTo = (area) => {
    setLogoPos((p) => ({ ...p, x: area.x, y: area.y, size: area.id === 'left_chest' ? 14 : (area.id === 'full_back' ? 40 : 28) }));
  };

  const renderPreviewBase64 = async () => {
    // Render current canvas to a PNG data URL (best effort: html2canvas-free fallback).
    // We snapshot the SVG + logo into a single canvas using <foreignObject> trick.
    try {
      const node = canvasRef.current;
      if (!node) return null;
      const w = node.clientWidth || 400;
      const h = node.clientHeight || 480;
      // Build a composite via canvas
      const canvas = document.createElement('canvas');
      canvas.width = w * 2; canvas.height = h * 2;
      const ctx = canvas.getContext('2d');
      ctx.scale(2, 2);
      // Fill bg
      ctx.fillStyle = '#0A0A0A';
      ctx.fillRect(0, 0, w, h);
      // Draw shirt as colored rect-shape using svg blob
      const svg = node.querySelector('svg')?.outerHTML;
      if (svg) {
        const img = new Image();
        const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        await new Promise((res) => { img.onload = res; img.src = url; });
        ctx.drawImage(img, 0, 0, w, h);
        URL.revokeObjectURL(url);
      }
      // Draw logo
      if (logoSrc) {
        const limg = new Image();
        limg.crossOrigin = 'anonymous';
        await new Promise((res, rej) => { limg.onload = res; limg.onerror = rej; limg.src = logoSrc; });
        const lw = (logoPos.size / 100) * w;
        const ratio = limg.height / Math.max(limg.width, 1);
        const lh = lw * ratio;
        const lx = (logoPos.x / 100) * w - lw / 2;
        const ly = (logoPos.y / 100) * h - lh / 2;
        ctx.drawImage(limg, lx, ly, lw, lh);
      }
      return canvas.toDataURL('image/png');
    } catch (e) {
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!contact.name || !contact.email) {
      toast.error('Please add your name and email');
      return;
    }
    setSubmitting(true);
    try {
      const preview = await renderPreviewBase64();
      const payload = {
        name: contact.name.trim(),
        email: contact.email.trim(),
        phone: contact.phone?.trim() || null,
        organization: contact.organization?.trim() || null,
        quantity: Number(quantity) || 0,
        deadline: contact.deadline || null,
        notes: contact.notes || null,
        design_summary: `${quantity} pcs · ${method} · ${numLocations} location(s) · ${colors} color(s)${preSelected ? ' · ' + preSelected.brand + ' ' + preSelected.styleID + ' (' + preSelected.color + ')' : ''}`,
        garment_type: preSelected ? preSelected.styleID : 'tshirt',
        garment_color: preSelected?.color || garmentColor,
        print_method: method,
        num_locations: Number(numLocations) || 1,
        colors: Number(colors) || 1,
        logo_base64: logoSrc || null,
        mockup_preview_base64: preview,
        estimated_total: pricing?.total_amount || null,
      };
      await axios.post('/public/design-request', payload);
      setSubmitted(true);
      toast.success('Design sent! We\'ll be in touch within 24 hours.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const lightText = useMemo(() => {
    // Decide if shirt color is light (so we adjust hint text color of mockup)
    try {
      const hex = garmentColor.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      return (r * 299 + g * 587 + b * 114) / 1000 > 160;
    } catch { return false; }
  }, [garmentColor]);

  // ===== Submitted thank-you state =====
  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6" data-testid="designer-success">
        <div className="max-w-md text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#D90000]/20 flex items-center justify-center">
            <CheckCircle2 className="w-8 h-8 text-[#D90000]" />
          </div>
          <h1 className="heading-font text-4xl font-black uppercase mb-3" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            Design Received
          </h1>
          <p className="text-white/70 mb-8">
            Thanks {contact.name.split(' ')[0]} — our team is reviewing your design now. You'll hear back from us within 24 hours with a formal quote.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={() => navigate('/')} className="bg-[#D90000] hover:bg-[#FF1A1A] rounded-sm font-bold uppercase tracking-wider h-12 px-6" data-testid="designer-back-home">
              Back to Home
            </Button>
            <Button onClick={() => window.location.reload()} variant="outline" className="rounded-sm font-bold uppercase tracking-wider h-12 px-6 border-white/30 text-white hover:bg-white hover:text-black" data-testid="designer-start-over">
              Design Another
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" style={{ fontFamily: "'DM Sans', sans-serif" }} data-testid="public-designer">
      <style>{`
        .heading-font { font-family: 'Barlow Condensed', sans-serif; letter-spacing: -0.01em; }
        .overline { font-family: 'Barlow Condensed', sans-serif; letter-spacing: 0.18em; text-transform: uppercase; }
      `}</style>

      {/* ===== HEADER ===== */}
      <header className="sticky top-0 z-50 bg-black/85 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="designer-nav-home">
            <BrandLogo height={36} className="h-9 w-auto" /><span style={{display:'none'}} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            <span className="heading-font text-xl font-black tracking-tight uppercase hidden sm:block">{brandLogo() ? '' : ''}</span>
          </Link>
          <Button onClick={() => navigate('/')} variant="ghost" className="text-white/70 hover:text-white hover:bg-white/10" data-testid="designer-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>
      </header>

      {/* ===== HERO STRIP ===== */}
      <div className="border-b border-white/10 bg-gradient-to-r from-[#0A0A0A] via-[#141414] to-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 py-10">
          <div className="overline text-[#D90000] text-xs mb-3 flex items-center gap-3">
            <span className="h-px w-12 bg-[#D90000]" /> Design Now · Instant Estimate
          </div>
          <h1 className="heading-font text-4xl sm:text-5xl font-black uppercase leading-[0.95]">
            {preSelected ? (
              <>Design your <span className="text-[#D90000]">{preSelected.brand} {preSelected.styleID}</span>.</>
            ) : (
              <>Design your shirt.<br /><span className="text-[#D90000]">See pricing instantly.</span></>
            )}
          </h1>
          <p className="text-white/60 mt-3 max-w-2xl">
            {preSelected
              ? `Now designing: ${preSelected.styleName} · ${preSelected.color}. Upload your logo, place it, and get pricing.`
              : "Upload your logo, drop it on the shirt, pick a method and quantity. We'll send a formal quote within 24 hours."}
          </p>
          {!preSelected && (
            <Link to="/catalog" data-testid="hero-browse-catalog-link" className="inline-flex items-center gap-2 mt-4 text-[#FF4444] hover:text-[#FF6666] text-sm font-semibold uppercase tracking-wider">
              Or browse the full catalog →
            </Link>
          )}
        </div>
      </div>

      {/* ===== DESIGNER GRID ===== */}
      <div className="max-w-7xl mx-auto px-6 py-10 grid lg:grid-cols-[1fr_1fr] gap-10">
        {/* LEFT — Canvas */}
        <div>
          <div
            ref={canvasRef}
            className="relative w-full mx-auto select-none"
            style={{ aspectRatio: '400/480', maxWidth: 460, background: 'radial-gradient(ellipse at center, #1a1a1a 0%, #0A0A0A 70%)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.08)' }}
            onMouseMove={onDrag}
            onMouseUp={endDrag}
            onMouseLeave={endDrag}
            onTouchMove={onDrag}
            onTouchEnd={endDrag}
            data-testid="designer-canvas"
          >
            <div className="absolute inset-0 flex items-center justify-center p-6 pointer-events-none">
              {preSelected?.imageUrl ? (
                <img src={preSelected.imageUrl} alt={preSelected.styleName} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              ) : (
                <TShirtSVG color={garmentColor} view={view} />
              )}
            </div>
            {logoSrc ? (
              <img
                src={logoSrc}
                alt="logo"
                onMouseDown={startDrag}
                onTouchStart={startDrag}
                draggable={false}
                className="absolute cursor-move"
                style={{
                  left: `${logoPos.x}%`,
                  top: `${logoPos.y}%`,
                  width: `${logoPos.size}%`,
                  transform: 'translate(-50%, -50%)',
                  filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.6))',
                  touchAction: 'none',
                }}
                data-testid="designer-logo"
              />
            ) : (
              <div className={`absolute inset-x-0 ${view === 'front' ? 'top-[40%]' : 'top-[50%]'} flex justify-center pointer-events-none`}>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="pointer-events-auto bg-black/60 backdrop-blur border border-dashed border-white/40 rounded-sm px-5 py-3 hover:bg-black/80 transition text-sm overline"
                  data-testid="designer-upload-trigger"
                >
                  <Upload className="w-4 h-4 inline-block mr-2" /> Upload Your Logo
                </button>
              </div>
            )}
          </div>

          {/* Canvas controls */}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <input ref={fileInputRef} type="file" accept="image/*" onChange={onUploadLogo} className="hidden" data-testid="designer-file-input" />
            <Button
              onClick={() => fileInputRef.current?.click()}
              variant="outline"
              className="rounded-sm border-white/20 text-white hover:bg-white/10"
              data-testid="designer-upload-btn"
            >
              <Upload className="w-4 h-4 mr-2" /> {logoSrc ? 'Replace Logo' : 'Upload Logo'}
            </Button>
            {logoSrc && (
              <>
                <Button onClick={() => setLogoPos((p) => ({ ...p, size: Math.max(8, p.size - 4) }))} variant="outline" className="rounded-sm border-white/20 text-white hover:bg-white/10" data-testid="designer-shrink">
                  <ZoomOut className="w-4 h-4" />
                </Button>
                <Button onClick={() => setLogoPos((p) => ({ ...p, size: Math.min(60, p.size + 4) }))} variant="outline" className="rounded-sm border-white/20 text-white hover:bg-white/10" data-testid="designer-enlarge">
                  <ZoomIn className="w-4 h-4" />
                </Button>
                <Button onClick={() => setLogoSrc(null)} variant="outline" className="rounded-sm border-white/20 text-red-400 hover:bg-red-500/10" data-testid="designer-remove-logo">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>

          {/* Snap placement */}
          {logoSrc && (
            <div className="mt-4">
              <div className="overline text-xs text-white/40 mb-2">Quick Placement</div>
              <div className="flex flex-wrap gap-2">
                {DESIGN_AREAS.map((a) => (
                  <button
                    key={a.id}
                    onClick={() => snapTo(a)}
                    className="text-xs uppercase tracking-wider border border-white/20 bg-white/5 hover:bg-white/10 px-3 py-2 rounded-sm transition"
                    data-testid={`designer-snap-${a.id}`}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-white/40 mt-2 flex items-center gap-1.5">
                <Move className="w-3 h-3" /> Drag the logo to fine-tune its position.
              </p>
            </div>
          )}

          {/* Garment color */}
          <div className="mt-8">
            <div className="overline text-xs text-white/50 mb-3">Garment Color</div>
            <div className="flex flex-wrap gap-2">
              {GARMENT_COLORS.map((c) => (
                <button
                  key={c.hex}
                  onClick={() => setGarmentColor(c.hex)}
                  className={`w-10 h-10 rounded-full border-2 transition ${garmentColor === c.hex ? 'border-[#D90000] ring-2 ring-[#D90000]/30' : 'border-white/20 hover:border-white/50'}`}
                  style={{ backgroundColor: c.hex }}
                  title={c.name}
                  data-testid={`designer-color-${c.name.toLowerCase().replace(/\s+/g,'-')}`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT — Options + Pricing + Contact */}
        <div className="space-y-8">
          {/* Method */}
          <div>
            <div className="overline text-xs text-white/50 mb-3">Decoration Method</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {METHODS.map((m) => (
                <button
                  key={m.id}
                  onClick={() => setMethod(m.id)}
                  className={`text-left p-3 rounded-sm border transition ${method === m.id ? 'border-[#D90000] bg-[#D90000]/10' : 'border-white/15 hover:border-white/40 bg-white/5'}`}
                  data-testid={`designer-method-${m.id}`}
                >
                  <div className="heading-font font-bold uppercase text-sm">{m.label}</div>
                  <div className="text-[11px] text-white/50 mt-0.5">{m.hint}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Quantity + locations + colors */}
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <Label className="overline text-xs text-white/50 mb-2 block">Quantity</Label>
              <Input
                type="number" min="1"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="bg-black border-white/15 text-white rounded-sm h-12 text-base font-bold"
                data-testid="designer-quantity"
              />
            </div>
            <div>
              <Label className="overline text-xs text-white/50 mb-2 block">Print Locations</Label>
              <Input
                type="number" min="1" max="4"
                value={numLocations}
                onChange={(e) => setNumLocations(e.target.value)}
                className="bg-black border-white/15 text-white rounded-sm h-12 text-base font-bold"
                data-testid="designer-locations"
              />
            </div>
            <div>
              <Label className="overline text-xs text-white/50 mb-2 block">
                {method === 'embroidery' ? 'Stitches (k)' : 'Colors / Design'}
              </Label>
              <Input
                type="number" min="1" max="8"
                value={colors}
                onChange={(e) => setColors(e.target.value)}
                className="bg-black border-white/15 text-white rounded-sm h-12 text-base font-bold"
                data-testid="designer-colors"
              />
            </div>
          </div>

          {/* Live Pricing */}
          <div className="border border-[#D90000]/40 bg-[#D90000]/5 rounded-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="overline text-xs text-[#D90000] flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5" /> Instant Estimate
              </div>
              {pricingLoading && <Loader2 className="w-4 h-4 text-white/40 animate-spin" />}
            </div>
            {pricing ? (
              <div className="space-y-2">
                <div className="flex justify-between text-sm text-white/70">
                  <span>Garments ({pricing.quantity})</span>
                  <span>${pricing.shirt_total.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-white/70">
                  <span>Decoration</span>
                  <span>${pricing.print_total.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-white/70">
                  <span>Setup Fees</span>
                  <span>${pricing.setup_fees.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-white/70">
                  <span>Tax ({(pricing.tax_rate * 100).toFixed(0)}%)</span>
                  <span>${pricing.tax_amount.toFixed(2)}</span>
                </div>
                <div className="border-t border-white/15 pt-3 mt-3 flex items-baseline justify-between">
                  <div>
                    <div className="overline text-xs text-white/50">Estimated Total</div>
                    <div className="text-xs text-white/40 mt-1">${(pricing.total_amount / Math.max(pricing.quantity, 1)).toFixed(2)} per piece</div>
                  </div>
                  <div className="heading-font text-4xl font-black text-white" data-testid="designer-total">
                    ${pricing.total_amount.toFixed(2)}
                  </div>
                </div>
                <p className="text-[11px] text-white/40 italic mt-2">{pricing.disclaimer}</p>
              </div>
            ) : (
              <div className="text-white/40 text-sm">Enter your quantity to see pricing.</div>
            )}
          </div>

          {/* Contact form */}
          <form onSubmit={handleSubmit} className="space-y-3 border-t border-white/10 pt-8">
            <div className="overline text-xs text-white/50 flex items-center gap-2">
              <Shirt className="w-3.5 h-3.5" /> Send Us Your Design
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Input
                placeholder="Full Name *"
                value={contact.name}
                onChange={(e) => setContact({ ...contact, name: e.target.value })}
                className="bg-black border-white/15 text-white placeholder:text-white/30 rounded-sm h-11"
                data-testid="designer-contact-name"
              />
              <Input
                type="email"
                placeholder="Email *"
                value={contact.email}
                onChange={(e) => setContact({ ...contact, email: e.target.value })}
                className="bg-black border-white/15 text-white placeholder:text-white/30 rounded-sm h-11"
                data-testid="designer-contact-email"
              />
              <Input
                placeholder="Phone"
                value={contact.phone}
                onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                className="bg-black border-white/15 text-white placeholder:text-white/30 rounded-sm h-11"
                data-testid="designer-contact-phone"
              />
              <Input
                placeholder="Organization / Team"
                value={contact.organization}
                onChange={(e) => setContact({ ...contact, organization: e.target.value })}
                className="bg-black border-white/15 text-white placeholder:text-white/30 rounded-sm h-11"
                data-testid="designer-contact-org"
              />
            </div>
            <Input
              placeholder="Deadline (optional)"
              value={contact.deadline}
              onChange={(e) => setContact({ ...contact, deadline: e.target.value })}
              className="bg-black border-white/15 text-white placeholder:text-white/30 rounded-sm h-11"
              data-testid="designer-contact-deadline"
            />
            <Textarea
              placeholder="Anything else we should know? (sizes, special requests, etc.)"
              value={contact.notes}
              onChange={(e) => setContact({ ...contact, notes: e.target.value })}
              rows={3}
              className="bg-black border-white/15 text-white placeholder:text-white/30 rounded-sm"
              data-testid="designer-contact-notes"
            />
            <Button
              type="submit"
              disabled={submitting}
              size="lg"
              className="w-full bg-[#D90000] hover:bg-[#FF1A1A] text-white rounded-sm font-bold uppercase tracking-wider h-14 text-base"
              data-testid="designer-submit-btn"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
              ) : (
                <><Send className="w-4 h-4 mr-2" /> Send My Design</>
              )}
            </Button>
            <p className="text-[11px] text-white/40 text-center">
              No commitment — we'll confirm final pricing after artwork review.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PublicDesigner;
